import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiService, ApiStatus } from "../api";
import { GenericIcon } from "../icons";
import { setVisitorId } from "../utils";

const POLL_INTERVAL_MS = 3000;

const CreateMicrositeLandingPage = () => {
  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [companyDescription, setCompanyDescription] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isGenerationInProgress, setIsGenerationInProgress] = useState(false);
  const [error, setError] = useState("");
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const suggestedDomainsRef = useRef<string[]>([]);

  const createTriggeredRef = useRef(false);
  const companyNameRef = useRef("");
  const pollIdRef = useRef("");

  const intervalIdRef = useRef<number>();
  const numErrors = useRef(0);

  const pollForStatus = useCallback(async () => {
    const { message, error, data } = await apiService.getCreateCompanyMicrositeStatus(pollIdRef.current);

    if (error || !data) {
      numErrors.current += 1;
      if (numErrors.current > 5) {
        window.clearTimeout(intervalIdRef.current);
        setIsGenerationInProgress(false);
        setError(message);
      }
    } else {
      numErrors.current = 0;
      setProgressPercentage(data.progressPercentage || 0);

      // data.companyDetails.isCompanyKnown = false;
      if (
        (data.status === ApiStatus.COMPLETED || data.status === ApiStatus.FAILED) &&
        !data.companyDetails?.isCompanyKnown
      ) {
        console.log("[DEBUG] Triggering additional info window", { data });
        createTriggeredRef.current = false;
        setShowAdditionalInfo(true);
        setIsGenerationInProgress(false);
      } else if (data.status === ApiStatus.COMPLETED) {
        companyNameRef.current = data.companyDetails?.companyName || "";
        setIsGenerationInProgress(false);
        setError("");
      } else if (data.status === ApiStatus.FAILED) {
        suggestedDomainsRef.current = data.suggestedDomains || [];
        setIsGenerationInProgress(false);
        setError(data.errors?.join(",") || "Error creating microsite");
      } else {
        intervalIdRef.current = window.setTimeout(() => {
          pollForStatus();
        }, POLL_INTERVAL_MS);
      }
    }
  }, []);

  const handleCompanyNameSubmit = useCallback(async () => {
    if (!companyName.trim()) {
      setError("Please enter a valid company name.");
      return;
    }

    if (showAdditionalInfo && (!companyDomain.trim() || !companyDescription.trim())) {
      setError("Please provide both company domain and description.");
      return;
    }
    setError("");

    createTriggeredRef.current = true;
    setIsGenerationInProgress(true);

    setVisitorId(companyName);
    const { message, error, data } = await apiService.createCompanyMicrosite(
      companyName,
      customPrompt,
      showAdditionalInfo ? companyDomain : undefined,
      showAdditionalInfo ? companyDescription : undefined
    );

    // Wait for 10 seconds before starting the first status check
    await new Promise(resolve => setTimeout(resolve, 3000));

    if (error) {
      setError(message);
      setIsGenerationInProgress(false);
    } else {
      pollIdRef.current = data.pollId;
      companyNameRef.current = data.companyName;
      pollForStatus();
    }
  }, [companyName, companyDomain, companyDescription, customPrompt, showAdditionalInfo, pollForStatus]);

  useEffect(
    () => () => {
      if (intervalIdRef.current) {
        window.clearTimeout(intervalIdRef.current);
      }
    },
    []
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleCompanyNameSubmit();
      e.preventDefault();
    }
  };

  const handleRetry = () => {
    createTriggeredRef.current = false;
    setError("");
    setShowAdditionalInfo(false);
    setCompanyDomain("");
    setCompanyDescription("");
  };

  const url = `${window.location.origin}/microsite/${companyNameRef.current}/${pollIdRef.current}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-center min-h-screen h-full max-w-screen bg-dark-gray-brand flex-col gap-14 text-light-primary-text py-20 overflow-y-auto overflow-x-hidden">
      <div className="flex text-red-brand text-[64px]">
        <GenericIcon
          className="mx-auto"
          iconName="bicycle-inline-red-lockup-rgb"
          size={64}
        />
      </div>

      <div className="flex flex-col gap-y-6 items-center justify-center">
        <div className="text-4xl font-bold">Your 24x7 AI Analyst Copilot</div>
        <div className="text-lg font-semibold text-center max-w-lg">
          Bicycle&#39;s AI analyzes your operational data 24/7, provides insights on revenue-critical issues and
          opportunities — and recommends fast, precise action.
        </div>
      </div>

      {!isGenerationInProgress && (
        <>
          <>
            {!error && (
              <>
                {!createTriggeredRef.current && (
                  <div className="bg-rose-50 p-6 rounded-2xl shadow-2xl w-1/2 flex flex-col gap-6">
                    <div className="text-2xl font-title font-bold text-dark-primary-text mb-4 text-center"></div>
                    <div className="flex flex-col gap-[8px] w-full">
                      {showAdditionalInfo ? (
                        <>
                          <p className="font-body text-sm text-dark-primary-text mb-4">
                            We need some additional information about {companyName}:
                          </p>
                          <p className="font-body text-sm text-dark-primary-text">Company Domain:</p>
                          <input
                            className="border rounded px-4 py-2 w-full text-normal placeholder:text-dark-secondary-text caret-dark-primary-text !text-dark-primary-text active:outline-red-brand focus:outline-red-brand active:shadow-blue-400 focus:shadow-blue-400 active:shadow-sm focus:shadow-sm"
                            onChange={e => setCompanyDomain(e.target.value)}
                            placeholder="e.g. mastercard.com"
                            type="text"
                            value={companyDomain}
                          />
                          <p className="font-body text-sm text-dark-primary-text mt-4">Company Description:</p>
                          <textarea
                            className="border rounded px-4 py-2 w-full text-normal placeholder:text-dark-secondary-text caret-dark-primary-text !text-dark-primary-text active:outline-red-brand focus:outline-red-brand active:shadow-blue-400 focus:shadow-blue-400 active:shadow-sm focus:shadow-sm"
                            onChange={e => setCompanyDescription(e.target.value)}
                            placeholder="Please provide a brief description of your company"
                            rows={4}
                            value={companyDescription}
                          />
                        </>
                      ) : (
                        <p className="font-body text-sm text-dark-primary-text">
                          Please enter your company&apos;s name:
                        </p>
                      )}
                      <input
                        className="border rounded px-4 py-2 w-full text-normal placeholder:text-dark-secondary-text caret-dark-primary-text !text-dark-primary-text active:outline-red-brand focus:outline-red-brand active:shadow-blue-400 focus:shadow-blue-400 active:shadow-sm focus:shadow-sm"
                        onChange={e => setCompanyName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. Mastercard"
                        type="text"
                        value={companyName}
                      />
                    </div>

                    <div className="flex flex-col gap-[8px] w-full">
                      <p className="font-body text-sm text-dark-primary-text">Tell us more about your use case:</p>
                      <textarea
                        className="border rounded px-4 py-2 w-full text-normal placeholder:text-dark-secondary-text caret-dark-primary-text !text-dark-primary-text active:outline-red-brand focus:outline-red-brand active:shadow-blue-400 focus:shadow-blue-400 active:shadow-sm focus:shadow-sm"
                        onChange={e => setCustomPrompt(e.target.value)}
                        onKeyDown={handleKeyDown as any}
                        placeholder="Tell us about your use case in as much detail as you can."
                        rows={4}
                        value={customPrompt}
                      />
                    </div>

                    <button
                      className="cursor-pointer bg-red-brand font-body text-white px-4 py-2 rounded w-fit m-auto disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!companyName || (showAdditionalInfo && (!companyDomain || !companyDescription))}
                      onClick={handleCompanyNameSubmit}
                    >
                      Create Website
                    </button>

                    {Boolean(error) && (
                      <div className="mt-4 p-3 bg-white rounded-md border border-red-300 flex flex-col text-sm text-gray-600 gap-y-2">
                        <div className="flex gap-x-2">
                          Error: <b>{error}</b>
                        </div>
                        <div className="flex gap-x-2">
                          Timestamp: <b>{new Date().toLocaleString()}</b>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {createTriggeredRef.current && (
                  <div className="p-6 bg-red-50 rounded-lg border border-red-200">
                    <div className="text-lg font-semibold text-red-brand">You can access bicycle here</div>

                    <div className="mt-6">
                      <div
                        className="flex items-center gap-3 p-4 bg-white rounded-lg border hover:border-red-200 transition-colors cursor-pointer group border-[rgba(248, 72, 94, 0.2)]"
                        onClick={() => window.open(url, "_blank")}
                      >
                        <span
                          className="text-xl font-medium flex-grow text-red-brand text-nowrap text-ellipsis w-full overflow-hidden"
                          title={url}
                        >
                          {url}
                        </span>

                        <button
                          className="p-2 hover:bg-red-50 rounded-md transition-colors relative cursor-pointer"
                          onClick={e => {
                            e.stopPropagation();
                            handleCopy();
                          }}
                          title="Copy URL"
                        >
                          {/* Copy Icon */}
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="#F8485E"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                            />
                          </svg>

                          {/* Copy feedback tooltip */}
                          {copied && (
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded">
                              Copied!
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {Boolean(error) && (
              <>
                <div className="p-6 bg-red-50 rounded-lg border w-3xl">
                  <div className="flex items-start gap-3">
                    {/* Error Icon */}
                    <svg
                      className="h-6 w-6 text-red-600 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                      />
                    </svg>

                    <div className="flex-grow">
                      <div className="text-lg font-semibold text-red-700">Unable to generate website</div>
                      <p className="mt-1 text-red-600">
                        An error occurred while attempting to generate your website. This might be due to a temporary
                        issue.
                      </p>

                      <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <button
                          className="cursor-pointer inline-flex items-center justify-center px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors font-medium"
                          onClick={handleRetry}
                        >
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                            />
                          </svg>
                          Try Again
                        </button>
                      </div>

                      {/* Error Details (Optional) */}
                      <div className="mt-4 p-3  rounded-md border border-red-600 flex flex-col text-sm text-red-600 gap-y-2">
                        <div className="flex gap-x-2">
                          Error: <b>{error}</b>
                        </div>
                        <div className="flex gap-x-2">
                          Timestamp: <b>{new Date().toLocaleString()}</b>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {Boolean(suggestedDomainsRef.current.length) && (
                  <>
                    <div className="bg-dark-secondary-text rounded-lg overflow-hidden shadow-xl w-3xl">
                      <div className="p-5 bg-gradient-to-r from-[#F8485E]/20 to-transparent border-b border-white/10">
                        <h3 className="text-lg font-semibold text-white">Similar alternatives available</h3>
                      </div>
                      <div className="p-5 grid gap-3 sm:grid-cols-2">
                        {suggestedDomainsRef.current.map((suggestion, index) => (
                          <button
                            className="flex items-center justify-between p-4 rounded-lg bg-gray-800/50 hover:bg-[#F8485E] group transition-all duration-200 cursor-pointer"
                            key={index}
                            onClick={() => {
                              setCompanyName(suggestion);
                              handleRetry();
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                                <svg
                                  className="h-4 w-4 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                  />
                                </svg>
                              </div>
                              <span className="text-white font-medium">{suggestion}</span>
                            </div>
                            <svg
                              className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                              />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        </>
      )}

      {isGenerationInProgress && (
        <>
          <div className="flex flex-col items-center justify-center gap-y-4 w-1/2 mx-auto">
            <div className="loader-container">
              <div className="loader">
                <svg
                  className="circle circle-left"
                  viewBox="0 0 24 24"
                >
                  <path
                    clipRule="evenodd"
                    d="M24.02 9.3C22.73 3.96 17.92 0 12.18 0C5.45 0 0 5.45 0 12.18C0 18.91 5.45 24.36 12.18 24.36C17.92 24.36 22.73 20.4 24.02 15.06H17.62C16.59 17.01 14.54 18.33 12.18 18.33C8.78 18.33 6.03 15.58 6.03 12.18C6.03 8.78 8.78 6.03 12.18 6.03C14.54 6.03 16.59 7.35 17.62 9.3H24.02Z"
                    fillRule="evenodd"
                  />
                </svg>
                <svg
                  className="circle circle-right"
                  viewBox="0 0 24 24"
                >
                  <path
                    clipRule="evenodd"
                    d="M24.02 9.3C22.73 3.96 17.92 0 12.18 0C5.45 0 0 5.45 0 12.18C0 18.91 5.45 24.36 12.18 24.36C17.92 24.36 22.73 20.4 24.02 15.06H17.62C16.59 17.01 14.54 18.33 12.18 18.33C8.78 18.33 6.03 15.58 6.03 12.18C6.03 8.78 8.78 6.03 12.18 6.03C14.54 6.03 16.59 7.35 17.62 9.3H24.02Z"
                    fillRule="evenodd"
                  />
                </svg>
                <div className="dot"></div>
              </div>
            </div>
            <div className={`w-full h-4 rounded-full bg-gray-700 text-white`}>
              <div
                className={`h-full rounded-full bg-red-brand transition-all duration-300 ease-in-out`}
                style={{ width: `${progressPercentage}%` }}
              >
                <span className="block px-2 text-xs font-semibold">{progressPercentage}%</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateMicrositeLandingPage;
